import API_BASE_URL from "../RestAPI";
import { qualityMock } from "./qualityMock";

/**
 * API layer for the Quality module — production bugs, production releases,
 * regression runs and the global test case library.
 *
 * Same shape as whatsNewApi.js: every response is {status, data, ...} and a
 * non-2xx response throws with the server's message, carrying the payload on
 * `err.payload` so screens can show field-level reasons.
 *
 * Until the `quality` Django app is deployed, the screens run against an
 * in-memory mock with the same contract (qualityMock.js). Turn it on with
 * REACT_APP_QUALITY_MOCK=true at build time, or localStorage.qualityMock = "1"
 * in a browser. The pages show a banner whenever the mock is in use, so sample
 * data is never mistaken for real bugs.
 */

const BASE = `${API_BASE_URL.replace(/\/$/, "")}/quality`;

export const isQualityMock = () => {
    if (process.env.REACT_APP_QUALITY_MOCK === "true") return true;
    try {
        return localStorage.getItem("qualityMock") === "1";
    } catch {
        return false;
    }
};

const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
};

const buildQuery = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val == null || val === "") return;
        qs.append(key, String(val));
    });
    const s = qs.toString();
    return s ? `?${s}` : "";
};

const handle = async (res, fallbackMsg) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || res.statusText || fallbackMsg);
        err.payload = data;
        throw err;
    }
    return data;
};

const call = async (path, { method = "GET", body, params } = {}, fallbackMsg = "Request failed") => {
    const res = await fetch(`${BASE}/${path}${buildQuery(params)}`, {
        method,
        headers: getAuthHeaders(),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    return handle(res, fallbackMsg);
};

const realApi = {
    // ── reference data ───────────────────────────────────
    // {modules:[{name, sub_modules:[]}], members:[], orgs:[], servers:[{code,label}]}
    getMeta: () => call("meta/", {}, "Failed to load reference data"),
    getDashboard: () => call("dashboard/", {}, "Failed to load the quality dashboard"),

    // ── production bugs ──────────────────────────────────
    getBugs: (params) => call("bugs/", { params }, "Failed to load production bugs"),
    getBug: (id) => call(`bugs/${id}/`, {}, "Failed to load the bug"),
    // {source, source_ref, org_name, server_code, module, severity, title, details}
    createBug: (payload) => call("bugs/", { method: "POST", body: payload }, "Failed to log the bug"),
    updateBug: (id, payload) => call(`bugs/${id}/`, { method: "PATCH", body: payload }, "Failed to save the bug"),
    // Needs "Delete Ticket"; refused (409) while the bug still has a live fix ticket.
    deleteBug: (id) => call(`bugs/${id}/`, { method: "DELETE" }, "Failed to delete the bug"),
    advanceBug: (id) => call(`bugs/${id}/advance/`, { method: "POST", body: {} }, "Failed to move the bug"),
    // type: "customer_support" | "error_tracking". Results pre-fill the bug form.
    searchSources: (type, q) => call("sources/", { params: { type, q } }, "Failed to search"),
    uploadAttachments: async (bugId, files) => {
        const form = new FormData();
        Array.from(files).forEach((f) => form.append("files", f));
        const headers = getAuthHeaders();
        delete headers["Content-Type"]; // let the browser set the multipart boundary
        const res = await fetch(`${BASE}/bugs/${bugId}/attachments/`, { method: "POST", headers, body: form });
        return handle(res, "Failed to upload files");
    },
    getAttachmentUrl: (id) => call(`attachments/${id}/`, {}, "Failed to open the file"),
    deleteAttachment: (id) => call(`attachments/${id}/`, { method: "DELETE" }, "Failed to remove the file"),
    getSuggestions: (id) => call(`bugs/${id}/suggestions/`, {}, "Failed to load release suggestions"),
    // {release_id, ticket_code} or {untraced_reason}
    traceBug: (id, payload) => call(`bugs/${id}/trace/`, { method: "POST", body: payload }, "Failed to save the trace"),
    clearTrace: (id) => call(`bugs/${id}/trace/`, { method: "DELETE" }, "Failed to clear the trace"),
    // {missed_test_case, category, process_step, why_not_detected, root_cause, contributing_factors}
    saveRca: (id, payload) => call(`bugs/${id}/rca/`, { method: "PUT", body: payload }, "Failed to save the analysis"),
    // {type, description, owner, due_date}
    addAction: (id, payload) => call(`bugs/${id}/actions/`, { method: "POST", body: payload }, "Failed to add the action"),
    createFixTicket: (id, sprintId) =>
        call(`bugs/${id}/fix-ticket/`, { method: "POST", body: sprintId ? { sprint_id: sprintId } : {} }, "Failed to create the fix ticket"),
    // Same rules as the Sprint screen: Production needs an approved Live Move.
    // Needs the "Delete Ticket" permission; refused (409) once the ticket is in a release.
    deleteFixTicket: (id) => call(`bugs/${id}/fix-ticket/`, { method: "DELETE" }, "Failed to delete the fix ticket"),
    setFixTicketStatus: (id, statusId) =>
        call(`bugs/${id}/fix-ticket/`, { method: "PATCH", body: { status_id: statusId } }, "Failed to change the status"),

    // ── production releases ──────────────────────────────
    getReleases: () => call("releases/", {}, "Failed to load releases"),
    getRelease: (id) => call(`releases/${id}/`, {}, "Failed to load the release"),
    // {result, waiver_reason}
    updateExecution: (id, payload) => call(`executions/${id}/`, { method: "PATCH", body: payload }, "Failed to save the result"),
    validateRelease: (id) => call(`releases/${id}/validate/`, { method: "POST", body: {} }, "Failed to validate the release"),

    // ── test case library ────────────────────────────────
    getTestCases: (params) => call("test-cases/", { params }, "Failed to load test cases"),
    similarTestCases: (title, exclude) => call("test-cases/similar/", { params: { title, exclude } }, "Failed to check for similar cases"),
    // {title, module, sub_module, case_type, priority, in_regression, critical, status,
    //  preconditions, test_data, steps:[{action, expected}], applies_to, setting_name,
    //  owner, tags, ticket_code?, bug_id?, action_id?}
    createTestCase: (payload) => call("test-cases/", { method: "POST", body: payload }, "Failed to add the test case"),
    updateTestCase: (id, payload) => call(`test-cases/${id}/`, { method: "PATCH", body: payload }, "Failed to save the test case"),
    // Sheet import. dryRun=true returns a row-by-row preview and saves nothing.
    // With sprintId every row needs a Ticket column (a ticket code from that
    // sprint) and each case is linked to its ticket.
    // With ticketId every row is linked to that one ticket.
    importTestCases: async (file, { dryRun = true, createMissing = false, sprintId = null, ticketId = null } = {}) => {
        const form = new FormData();
        form.append("file", file);
        form.append("dry_run", dryRun ? "1" : "0");
        form.append("create_missing", createMissing ? "1" : "0");
        if (sprintId) form.append("sprint_id", String(sprintId));
        if (ticketId) form.append("ticket_id", String(ticketId));
        const headers = getAuthHeaders();
        delete headers["Content-Type"]; // multipart boundary is set by the browser
        const res = await fetch(`${BASE}/test-cases/import/`, { method: "POST", headers, body: form });
        return handle(res, "Failed to read the file");
    },
    // The .xlsx template with the sheet's columns, as a Blob.
    downloadTemplate: async (sprintId = null, ticketId = null) => {
        const headers = getAuthHeaders();
        delete headers["Content-Type"];
        const qs = sprintId ? `?sprint_id=${sprintId}` : ticketId ? `?ticket_id=${ticketId}` : "";
        const res = await fetch(`${BASE}/test-cases/template/${qs}`, { headers });
        if (!res.ok) throw new Error("Failed to download the template");
        return res.blob();
    },
    // 409 with code "in_use" when the case is release history; deprecate it instead.
    deleteTestCase: (id) => call(`test-cases/${id}/`, { method: "DELETE" }, "Failed to delete the test case"),

    // ── a sprint ticket's Test Cases tab ─────────────────
    getTicketTestCases: (ticketId) => call(`tickets/${ticketId}/test-cases/`, {}, "Failed to load the ticket's test cases"),
    linkTicketTestCase: (ticketId, code) =>
        call(`tickets/${ticketId}/test-cases/`, { method: "POST", body: { code } }, "Failed to link the test case"),
    unlinkTicketTestCase: (ticketId, code) =>
        call(`tickets/${ticketId}/test-cases/`, { method: "DELETE", params: { code } }, "Failed to unlink the test case"),
};

export const qualityApi = new Proxy(realApi, {
    // Resolved per call rather than at import, so flipping the localStorage
    // switch takes effect on the next request without a rebuild.
    get: (target, key) => (isQualityMock() ? qualityMock[key] : target[key]),
});
